import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  type GlobalTabType,
  type IGlobalTab,
  type IInnerTab,
  type PageDetails,
  type PresentationType,
  type TabGroup,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { selectShowtimeDomain } from '@carboncopyco/data';
import type { ExternalCloneSession } from '../../shared/cloner/types';
import { AnalyticsEvents } from '../../shared/constants/analytics.events';
import { isOverlayTab, isTabTypeDemo } from '../../shared/globalTabUtil';
import {
  getEditsKey,
  getRulesKey,
  getVariablesKey,
} from '../../shared/interceptor-state-keys';
import { getRandomNumber } from '../../util/random';
import { getNextTabIndex, insertOnIndex } from '../../util/tab-util';
import { useFocusAppService } from '../hooks/useFocusAppService';
import useSwitchTab from '../hooks/useSwitchTab';
import {
  debuggerInterceptorState,
  isHandledInComponent,
} from '../shared/utils';
import { useTabs, useUser, useWorkspaces } from '../store/context';
import rendererService from './RendererService';

export interface ICreateGlobalTabData {
  title?: string;
  type: TabType;
  url?: string;
  referrer?: string;
  cloneId?: string;
  demoId?: string;
  folderId?: string;
  organizationId?: string;
  payload?: any;
  hidden?: boolean;
  hoverTitle?: string;
  workspaceId?: string;
  externalCloneSession?: ExternalCloneSession;
  group?: TabGroup;
  routeTo?: string;
  analyticsSessionId?: string;
  presentationId?: string;
  presentationType?: PresentationType;
  playId?: string;
  forClient?: string;
}

export const HOME_TAB_ID = 1;

export function useTabManager() {
  const {
    globalTabs,
    setGlobalTabs,
    setActiveGlobalTabId,
    activeGlobalTabId,
    globalTabsRef: globalTabsRefFromContext,
  } = useTabs();
  const showtimeDomain = useSelector(selectShowtimeDomain);
  const activeGlobalTabIdRef = useRef<number>();
  useEffect(() => {
    activeGlobalTabIdRef.current = activeGlobalTabId;
  }, [activeGlobalTabId]);
  const switchTab = useSwitchTab();
  const { activeWorkspace, defaultWorkspace } = useWorkspaces();

  const { hideOpenDropdown } = useFocusAppService();
  const createBasicTab = () => {
    return {
      id: getRandomNumber(),
      type: TabType.BASIC,
    } as IGlobalTab;
  };
  const { trackEvent } = useAnalytics();
  const { personas } = useUser();

  const isHomeTab = (tabId = activeGlobalTabId) => {
    return tabId === 1;
  };

  const getTabFocusId = (
    tab: ICreateGlobalTabData | IGlobalTab
  ): number | null => {
    let check;
    switch (tab.type) {
      case TabType.DEMO_MOBILE_PRESENTATION:
      case TabType.GROUP_DEMO_MOBILE_PRESENTATION:
      case TabType.PRESENT_DEMO:
      case TabType.PREVIEW_DEMO:
      case TabType.PREVIEW_OVERLAY:
        check = tab.payload?.presentation?.id || tab.payload.demo.id;
        break;
      case TabType.DEMO_EDITOR:
      case TabType.MOBILE_DEMO_EDITOR:
      case TabType.OVERLAY_EDITOR:
        check = tab.payload?.demo.id;
        break;
      case TabType.PREVIEW_CLONE:
        check = tab?.cloneId || tab.payload?.cloneId;
        break;
      case TabType.TOURS_EDITOR:
      case TabType.TOURS_PREVIEW:
        check = tab.payload?.tour?.id;
        break;
      case TabType.TOURS_CREATE:
      case TabType.LIVE_TOURS_CREATE:
        check =
          tab.payload?.tourData?.originalTourId ||
          (tab.payload?.source?.originalTourId !== 'undefined' &&
            tab.payload?.source?.originalTourId);
        break;
      case TabType.GROUP_DEMO_PRESENTATION:
      case TabType.GROUP_DEMO_PREVIEW:
      case TabType.GROUP_TOUR_PRESENTATION:
      case TabType.GROUP_TOUR_PREVIEW:
      case TabType.GROUP_URL_PRESENTATION:
        check = !!tab.group?.id;
        break;
      case TabType.PRESENTER_DEEPLINK_PLAYBOOK:
      case TabType.PRESENTER_DEEPLINK_DEMO:
        check = tab.payload.asset.id;
        break;
      default:
        break;
    }
    return check ? Number(check) : null;
  };

  const createInnerTabObject = (details: PageDetails): IInnerTab => {
    return {
      id: getRandomNumber(),
      url: details.url,
      type: TabType.INNER,
      ...(details.referrer && {
        referrer: details.referrer,
      }),
      ...(details.postBody && {
        postBody: details?.postBody,
      }),
    };
  };

  const createCloneTab = (globalTabData: ICreateGlobalTabData) => {
    const innerTabs: IInnerTab[] = [];
    const {
      type,
      url,
      cloneId,
      organizationId,
      payload: clone,
      externalCloneSession,
      folderId,
    } = globalTabData;
    const tab: IGlobalTab = {
      id: getRandomNumber(),
      title: url ? clone?.recording?.recordName : 'Untitled clone',
      type:
        type === TabType.UPDATE_CLONE ? TabType.UPDATE_CLONE : TabType.CLONE,
      url: url || externalCloneSession?.firstUrl,
      cloneId,
      innerTabs,
      closedInnerTabs: [],
      organizationId,
      payload: { clone, externalCloneSession },
      folderId,
    };
    if (type === TabType.UPDATE_CLONE && url) {
      const innerTab = createInnerTabObject({ url });
      tab.innerTabs?.push(innerTab);
      tab.activeInnerTabId = innerTab.id;
    }
    return tab;
  };

  const createBrowserWindowTab = (globalTabData: ICreateGlobalTabData) => {
    const innerTabs: IInnerTab[] = [];
    const {
      url,
      payload,
      title,
      group,
      analyticsSessionId,
      presentationId,
      presentationType,
      playId,
      forClient,
      createGlobalTabOnElectron,
    } = globalTabData as ICreateGlobalTabData & GlobalTabType;
    const tab: IGlobalTab & GlobalTabType = {
      id: getRandomNumber(),
      title,
      type: TabType.BROWSER_WINDOW,
      url,
      innerTabs,
      payload,
      group,
      analyticsSessionId,
      forClient,
      presentationId,
      presentationType,
      playId,
      createGlobalTabOnElectron,
    };
    if (url) {
      const innerTab = createInnerTabObject({ url });
      tab.innerTabs?.push(innerTab);
      tab.activeInnerTabId = innerTab.id;
    }
    return tab;
  };

  const createLiveEditorTab = (globalTabData: ICreateGlobalTabData) => {
    const innerTabs: IInnerTab[] = [];
    const { url, cloneId, organizationId, payload, folderId } = globalTabData;

    const tab: IGlobalTab = {
      id: getRandomNumber(),
      title: 'Untitled overlay',
      type: TabType.OVERLAY_NEW,
      url,
      payload,
      cloneId,
      innerTabs,
      closedInnerTabs: [],
      organizationId,
      folderId,
    };
    return tab;
  };

  const createPreviewTab = (globalTabData: ICreateGlobalTabData) => {
    const { url, referrer, cloneId, payload: clone, group } = globalTabData;
    const innerTabs: IInnerTab[] = [];
    const id = getRandomNumber();
    const tab: IGlobalTab = {
      id,
      title: clone.recordName,
      type: TabType.PREVIEW_CLONE,
      url,
      payload: { cloneId, tabId: id, clone },
      innerTabs,
      closedInnerTabs: [],
      group,
    };

    if (url) {
      const innerTab = createInnerTabObject({
        url,
        referrer,
      });
      tab.innerTabs?.push(innerTab);
      tab.activeInnerTabId = innerTab.id;
    }

    return tab;
  };

  const isTourCreatingTab = (tab: IGlobalTab | ICreateGlobalTabData) => {
    return (
      tab.type === TabType.TOURS_CREATE ||
      tab.type === TabType.LIVE_TOURS_CREATE
    );
  };

  const checkIfTabExists = (
    globalTabData: ICreateGlobalTabData
  ): IGlobalTab | null => {
    const focusedTabId = getTabFocusId(globalTabData);

    if (!focusedTabId) return null;
    const isNewTabTourCreatingTab = isTourCreatingTab(globalTabData);

    return (
      globalTabsRefFromContext.current?.find((tab) => {
        const isSameTitle =
          tab?.title && globalTabData.title
            ? tab.title === globalTabData.title
            : true;
        const isSameType = tab.type === globalTabData.type;

        if (isNewTabTourCreatingTab && isTourCreatingTab(tab)) {
          return getTabFocusId(tab) === focusedTabId;
        }

        return isSameTitle && isSameType && getTabFocusId(tab) === focusedTabId;
      }) ?? null
    );
  };

  const createDemoTab = ({
    type,
    url,
    referrer,
    cloneId,
    organizationId,
    payload,
    group,
    routeTo,
    title,
    hoverTitle,
    analyticsSessionId,
    presentationId,
    presentationType,
    playId,
  }: ICreateGlobalTabData) => {
    const innerTabs: IInnerTab[] = [];

    const tab: IGlobalTab = {
      id: getRandomNumber(),
      title,
      type,
      url,
      cloneId,
      innerTabs,
      closedInnerTabs: [],
      organizationId,
      payload,
      hidden: !!payload?.presentation?.hidden,
      hoverTitle,
      group,
      routeTo,
      analyticsSessionId,
      presentationId,
      presentationType,
      playId,
    };

    if (url) {
      const innerTab = createInnerTabObject({
        url,
        referrer,
      });
      tab.innerTabs?.push(innerTab);
      tab.activeInnerTabId = innerTab.id;
    }

    return tab;
  };

  const createEditorTab = (globalTabData: ICreateGlobalTabData) => {
    const innerTabs: IInnerTab[] = [];
    const { url, referrer, title, type, payload, group } = globalTabData;

    const tab: IGlobalTab = {
      id: getRandomNumber(),
      title,
      url,
      type,
      innerTabs,
      closedInnerTabs: [],
      payload,
      group,
    };
    if (!url) {
      return tab;
    }

    const innerTab = createInnerTabObject({
      url,
      referrer,
    });
    tab.innerTabs?.push(innerTab);
    tab.activeInnerTabId = innerTab.id;

    return tab;
  };

  const createToursTab = (globalTabData: ICreateGlobalTabData) => {
    const innerTabs: IInnerTab[] = [];
    const {
      url,
      referrer,
      title,
      payload,
      workspaceId,
      organizationId,
      demoId,
      folderId,
      group,
      analyticsSessionId,
      presentationId,
      presentationType,
      playId,
      createGlobalTabOnElectron,
      forClient,
    } = globalTabData as ICreateGlobalTabData & GlobalTabType;

    const tab: IGlobalTab & GlobalTabType = {
      id: getRandomNumber(),
      title,
      url,
      type: globalTabData.type,
      innerTabs,
      closedInnerTabs: [],
      payload,
      workspaceId,
      organizationId,
      demoId,
      folderId,
      group,
      analyticsSessionId,
      presentationId,
      presentationType,
      playId,
      createGlobalTabOnElectron,
      forClient,
    };
    if (!url) {
      return tab;
    }

    const innerTab = createInnerTabObject({
      url,
      referrer,
    });
    tab.innerTabs?.push(innerTab);
    tab.activeInnerTabId = innerTab.id;

    return tab;
  };

  const createTabIfNotExists = (
    globalTabData: ICreateGlobalTabData,
    create: (data: ICreateGlobalTabData) => IGlobalTab,
    replaceExistingUrl = true
  ) => {
    if (globalTabData.group) {
      const isExistingTabGroup = globalTabsRefFromContext.current?.some(
        (tab) => tab.group && tab.group?.id === globalTabData.group?.id
      );

      const isInitialised = globalTabsRefFromContext.current?.some(
        (tab) =>
          tab.group?.mergingOptions &&
          globalTabData.group?.mergingOptions &&
          tab.group.id === globalTabData.group.id &&
          tab.group.mergingOptions.key ===
            globalTabData.group.mergingOptions.key
      );

      if (isExistingTabGroup && isInitialised) {
        const tabToSwitchTo = globalTabsRefFromContext.current?.find(
          (tab) =>
            tab.group &&
            tab.group.id === globalTabData.group?.id &&
            tab.group.isVisibleIfGroupIsInactive
        );
        if (tabToSwitchTo) {
          switchTab(tabToSwitchTo);
          return {} as IGlobalTab;
        }
      } else {
        return create(globalTabData);
      }
    }

    const isTabExists = checkIfTabExists(globalTabData);
    if (isTabExists && !isTabExists?.hidden && isTabExists?.id) {
      if (
        globalTabData?.url &&
        isTabExists?.url !== globalTabData?.url &&
        replaceExistingUrl
      ) {
        const url = decodeURIComponent(globalTabData.url);
        const activeInnerTab = isTabExists.innerTabs.find(
          (innerTab) => innerTab.id === isTabExists.activeInnerTabId
        ) as IInnerTab;
        rendererService.navigateToUrInsideInnerTab(url, activeInnerTab);
      }
      setActiveGlobalTabId(isTabExists.id);
      return {} as IGlobalTab;
    }
    return create(globalTabData);
  };

  const getGlobalTabById = (id: number): IGlobalTab => {
    return globalTabs.find((tab: IGlobalTab) => tab.id === id) as IGlobalTab;
  };

  const createMobileTab = (
    type: TabType,
    url: string,
    tabData: ICreateGlobalTabData
  ) => {
    const innerTab = createInnerTabObject({ url });

    return {
      ...(tabData as ICreateGlobalTabData & GlobalTabType),
      type,
      id: getRandomNumber(),
      activeInnerTabId: innerTab.id,
      innerTabs: [innerTab],
      isMobileTab: true,
    };
  };
  const getMobileCloneUrl = (token: string) => {
    return `https://recorder.${showtimeDomain}/register?demosgw_token=${token}`;
  };

  const createMobilePreviewTab = (tabData: ICreateGlobalTabData) => {
    const url = getMobileCloneUrl(tabData.payload.playerToken);
    return createMobileTab(TabType.DEMO_MOBILE_PREVIEW, url, tabData);
  };

  const createMobileEditorTab = (tabData: ICreateGlobalTabData) => {
    const url = getMobileCloneUrl(tabData.payload.demo.playerToken);
    return createEditorTab({
      ...tabData,
      type: TabType.MOBILE_DEMO_EDITOR,
      url,
    });
  };

  const createMobilePresentationTab = (tabData: ICreateGlobalTabData) => {
    const url = getMobileCloneUrl(tabData.payload.demo.playerToken);
    return createMobileTab(TabType.DEMO_MOBILE_PRESENTATION, url, tabData);
  };

  const createGroupDemoMobilePresentationTab = (
    tabData: ICreateGlobalTabData
  ) => {
    const url = getMobileCloneUrl(tabData.payload.presentation.playerToken);
    return createMobileTab(
      TabType.GROUP_DEMO_MOBILE_PRESENTATION,
      url,
      tabData
    );
  };
  const createGroupDemoMobilePreviewTab = (tabData: ICreateGlobalTabData) => {
    const url = getMobileCloneUrl(tabData.payload.presentation.playerToken);
    return createMobileTab(TabType.GROUP_DEMO_MOBILE_PREVIEW, url, tabData);
  };

  const createGlobalTab = <T>(globalTabData: ICreateGlobalTabData & T) => {
    let tab = {} as IGlobalTab | (IGlobalTab & T);
    const { type, url, cloneId, workspaceId } = globalTabData;
    switch (type) {
      case TabType.BASIC:
        tab = createBasicTab();
        break;
      case TabType.UPDATE_CLONE:
      case TabType.CLONE:
        tab = createCloneTab(globalTabData);
        break;
      case TabType.OVERLAY_NEW:
        tab = createLiveEditorTab(globalTabData);
        break;
      case TabType.OVERLAY_EDITOR:
      case TabType.DEMO_EDITOR:
        tab = createTabIfNotExists(
          globalTabData,
          globalTabData.payload?.demo?.recordingType === 'mobile_recording'
            ? createMobileEditorTab
            : createEditorTab
        );
        break;
      case TabType.LIVE_TOURS_CREATE:
      case TabType.TOURS_CREATE:
      case TabType.TOURS_PREVIEW:
      case TabType.TOURS_EDITOR:
      case TabType.GROUP_TOUR_PRESENTATION:
      case TabType.GROUP_TOUR_PREVIEW: {
        tab = createTabIfNotExists(globalTabData, createToursTab, false);
        break;
      }
      case TabType.PREVIEW_CLONE:
        if (cloneId && url) {
          tab = createTabIfNotExists(
            globalTabData,
            globalTabData.payload?.recordingType === 'mobile_recording'
              ? createMobilePreviewTab
              : createPreviewTab
          );
        }
        break;
      case TabType.GROUP_DEMO_PREVIEW:
        tab = createTabIfNotExists(
          globalTabData,
          globalTabData.payload?.presentation?.recordingType ===
            'mobile_recording'
            ? createGroupDemoMobilePreviewTab
            : createDemoTab
        );
        break;
      case TabType.GROUP_DEMO_PRESENTATION:
        tab = createTabIfNotExists(
          globalTabData,
          globalTabData.payload?.presentation?.recordingType ===
            'mobile_recording'
            ? createGroupDemoMobilePresentationTab
            : createDemoTab
        );
        break;
      case TabType.PRESENT_DEMO:
      case TabType.PREVIEW_DEMO:
      case TabType.PREVIEW_OVERLAY:
      case TabType.PRESENTER_DEEPLINK_DEMO:
      case TabType.PRESENTER_DEEPLINK_PLAYBOOK:
        tab = createTabIfNotExists(
          globalTabData,
          globalTabData.payload?.demo?.recordingType === 'mobile_recording'
            ? createMobilePresentationTab
            : createDemoTab
        );
        break;
      case TabType.BROWSER_WINDOW:
        tab = createTabIfNotExists(globalTabData, createBrowserWindowTab);
        break;
      default:
        tab = createBasicTab();
        break;
    }
    tab.closedInnerTabs = [];

    let canCreateTab = true;
    if (tab?.id) {
      // If there is deeplink workspace, use it
      // If no deeplink use active workspace
      // If no active workspace, use default workspace
      const whichWorkspaceId =
        workspaceId ??
        (activeWorkspace.current?.id !== ''
          ? activeWorkspace.current.id
          : defaultWorkspace?.id);

      if (!whichWorkspaceId) {
        throw new Error('NO WORKSPACE ID');
      }

      tab.workspaceId = whichWorkspaceId;
      tab.personaId = personas?.defaultId;

      const handleCreateGroupTab = () => {
        const globalTabsFromContextRef = globalTabsRefFromContext.current;
        const groupTabs = globalTabsFromContextRef.filter(
          (t) => t.group && t.group?.id === tab.group?.id
        );
        const isFirstGroupTab = groupTabs.length === 0;

        // If it's a part of tabs that opens simultaneously and
        // previous tab is closed during creation, then do nothing
        const opensSimultaneouslyTab = tab.group?.mergingOptions?.allowed;
        if (opensSimultaneouslyTab) {
          const isFirstOpenSimultaneouslyTab =
            tab.group?.mergingOptions?.index === 0;

          if (!isFirstOpenSimultaneouslyTab) {
            const hasPreviousTab = groupTabs.some(
              (t) =>
                t.group?.mergingOptions &&
                tab.group?.mergingOptions &&
                t.group.mergingOptions.index ===
                  tab.group.mergingOptions.index - 1
            );

            const previousTabIsClosedDuringCreation = !hasPreviousTab;
            if (previousTabIsClosedDuringCreation) {
              canCreateTab = false;
              return;
            }
          }
        }

        let sortedTabs: IGlobalTab[];
        if (isFirstGroupTab) {
          sortedTabs = [...globalTabsFromContextRef, tab];
        } else {
          const lastGroupTab = groupTabs[groupTabs.length - 1];
          const indexOfLastGroupTab = globalTabsFromContextRef.findIndex(
            (t) => t.id === lastGroupTab.id
          );

          sortedTabs = insertOnIndex(
            indexOfLastGroupTab + 1,
            tab,
            globalTabsFromContextRef
          );
        }

        const activeGlobalTab = sortedTabs.find(
          (t) => t.id === activeGlobalTabIdRef.current
        ) as IGlobalTab;

        const getTabToSetActive = () => {
          if (tab.group?.focusOnCreate || isFirstGroupTab) return tab;
          return activeGlobalTab;
        };

        const tabToSetToActive = getTabToSetActive();
        activeGlobalTabIdRef.current = tabToSetToActive.id;

        switchTab(tabToSetToActive, {
          globalTabs: sortedTabs,
        });
      };

      if (tab.group) {
        handleCreateGroupTab();
      } else {
        setGlobalTabs((oldGlobalTabs: IGlobalTab[]) => {
          return switchTab(tab, {
            globalTabs: [...oldGlobalTabs, tab],
          });
        });
      }

      // TODO: remove this and call this from the first component that is rendered
      // for that particular tab type. For example, for the PREVIEW_OVERLAY tab type,
      // we call this from the PreviewDemoScreen component after we wait for all
      // the data to be loaded.
      if (!isHandledInComponent(tab) && canCreateTab) {
        hideOpenDropdown();
        rendererService.createGlobalTab(tab);
      }
    }

    if (canCreateTab) {
      trackEvent(AnalyticsEvents.CLICK_CREATE_GLOBAL_TAB, {
        location: 'desktop',
        variables: {
          tabType: tab.type,
          url: tab.url,
          tabId: tab.id,
        },
      });
    }

    return tab;
  };

  const getGlobalTabsWithPersona = (personaId: string) =>
    globalTabs.filter((tab) => tab.personaId === personaId);

  const resetPersona = (personaId: string) => {
    const globalTabsWithPersona = getGlobalTabsWithPersona(personaId);
    window.dsDesktop?.user.resetPersona(globalTabsWithPersona, personaId);
  };

  const deletePersona = (personaId: string) => {
    const globalTabsWithPersona = getGlobalTabsWithPersona(personaId);
    window.dsDesktop?.user.deletePersona(globalTabsWithPersona, personaId);
  };

  const navigateGlobalTab = (tab: IGlobalTab) => {
    rendererService.navigateGlobalTab(tab);
  };

  const navigateInnerTab = (innerTab: IInnerTab, globalTab: IGlobalTab) => {
    rendererService.navigateInnerTab(innerTab);
    setGlobalTabs((oldGlobalTabs: IGlobalTab[]) => {
      const newGlobalTabs = [...oldGlobalTabs];
      const newGlobalTab = { ...globalTab };
      newGlobalTab.activeInnerTabId = innerTab.id;
      newGlobalTabs[oldGlobalTabs.findIndex((t) => t.id === globalTab.id)] =
        newGlobalTab;
      return newGlobalTabs;
    });
  };

  const createInnerTab = (details: PageDetails) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setActiveGlobalTabId((oldActiveGlobalTabId: number) => {
      setGlobalTabs((oldGlobalTabs: IGlobalTab[]) => {
        const updatedGlobalTab = oldGlobalTabs.find(
          (tab) => tab.id === oldActiveGlobalTabId
        ) as IGlobalTab;
        const innerTab = createInnerTabObject(details);
        updatedGlobalTab.innerTabs?.push(innerTab);
        updatedGlobalTab.activeInnerTabId = innerTab.id;
        rendererService.createInnerTab(updatedGlobalTab, innerTab);
        return oldGlobalTabs.map((tab) =>
          tab.id === oldActiveGlobalTabId ? updatedGlobalTab : tab
        );
      });
      return oldActiveGlobalTabId;
    });
  };

  const closeGlobalTabs = async (tabs: IGlobalTab[]) => {
    const tabIds = tabs.map((tab) => tab.id);

    const newGlobalTabs = globalTabs.filter(
      (globalTab) => !tabIds.includes(globalTab.id)
    );

    setGlobalTabs(newGlobalTabs);

    const listOfPromises: Array<Promise<void>> = [];

    const speakerPanelId = tabs?.[0]?.group?.id;
    if (speakerPanelId) {
      await window.dsDesktop.speakerPanel.removeData(speakerPanelId);
    }

    tabs.forEach((tab) => {
      listOfPromises.push(rendererService.closeGlobalTab(tab));

      if (isOverlayTab(tab)) {
        debuggerInterceptorState.clearStateForKeys([
          getRulesKey(tab.id),
          getEditsKey(tab.id),
          getVariablesKey(tab.id),
        ]);
      }
    });
    await Promise.allSettled(listOfPromises);
  };

  const closeAllGlobalTabs = async () => {
    const newGlobalTabs = globalTabs.filter(
      (globalTab) => globalTab.type === 'basic'
    );
    setGlobalTabs(newGlobalTabs);
    setActiveGlobalTabId(newGlobalTabs[0].id);

    const listOfPromises: Array<Promise<void>> = [];

    globalTabs.forEach((tab) => {
      listOfPromises.push(rendererService.closeGlobalTab(tab));
      if (isTabTypeDemo(tab.type) && typeof trackEvent === 'function') {
        trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EXIT_PRESENTATION, {
          location: 'global_tabs',
        });
      }
      if (isOverlayTab(tab)) {
        debuggerInterceptorState.clearStateForKeys([
          getRulesKey(tab.id),
          getEditsKey(tab.id),
          getVariablesKey(tab.id),
        ]);
      }
    });
    await Promise.allSettled(listOfPromises);
  };

  const switchGlobalTabSession = (tab: IGlobalTab, personaId: string) => {
    window.dsDesktop?.user.switchActivePersona({
      personaId,
      tab,
    });

    const activeInnerTab = tab.innerTabs.find(
      (innerTab) => innerTab.id === tab.activeInnerTabId
    );
    if (!activeInnerTab) return;
    setGlobalTabs((oldGlobalTabs: IGlobalTab[]) => {
      const newGlobalTabs = oldGlobalTabs.map((globalTab) => {
        if (globalTab.id === tab.id) {
          return {
            ...globalTab,
            personaId,
          };
        }
        return globalTab;
      });
      return newGlobalTabs;
    });
    trackEvent(AnalyticsEvents.CLICK_SWITCH_PERSONA, {
      location: 'global_tabs',
    });
  };

  const getNextTabToSwitchTo = (tabId: number) => {
    if (!globalTabsRefFromContext.current) return globalTabs[0];

    const visibleGlobalTabs = globalTabsRefFromContext.current.filter(
      (globalTab) => !globalTab.hidden
    );

    // find index of closed tab in visible tabs
    const closedTabIndex = visibleGlobalTabs.findIndex(
      (globalTab) => globalTab.id === tabId
    );

    const filteredVisibleList = visibleGlobalTabs.filter(
      (globalTab) => globalTab.id !== tabId
    );

    // Take next tab or last visible tab if closed tab was last.
    // If there is no visible tabs (Home tab has hidden: true value), take home tab
    return (
      filteredVisibleList[closedTabIndex] ||
      filteredVisibleList[filteredVisibleList.length - 1] ||
      globalTabs[0]
    );
  };

  const getGlobalTabIndexById = (tabId: number) =>
    globalTabs.findIndex((globalTab: IGlobalTab) => globalTab.id === tabId);

  const switchToHomeTab = () => {
    switchTab(globalTabs[0]);
  };

  const closeGlobalTab = (id: number) => {
    if (isHomeTab(id)) return;
    const tabIndex = getGlobalTabIndexById(id);
    const isTabActive = id === activeGlobalTabId;
    const newGlobalTabs = globalTabsRefFromContext.current.filter(
      (globalTab) => globalTab.id !== id
    );

    if (isTabActive) {
      const tabToSwitchTo = getNextTabToSwitchTo(id);
      switchTab(tabToSwitchTo, {
        globalTabs: newGlobalTabs,
      });
    } else {
      setGlobalTabs(newGlobalTabs);
    }

    trackEvent(AnalyticsEvents.CLICK_CLOSE_GLOBAL_TAB, {
      location: 'desktop',
      variables: {
        tabType: globalTabs[tabIndex].type,
        url: globalTabs[tabIndex].url,
      },
    });

    if (globalTabs[tabIndex]) {
      rendererService.closeGlobalTab(globalTabs[tabIndex]);
      if (isTabTypeDemo(globalTabs[tabIndex].type)) {
        trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EXIT_PRESENTATION, {
          location: 'global_tabs',
        });
      }
    }
  };

  const closeInnerTab = (id: number) => {
    setGlobalTabs((oldGlobalTabs: IGlobalTab[]) => {
      const globalTab = oldGlobalTabs.find(
        (tab) => tab.id === activeGlobalTabId
      );
      if (!globalTab?.innerTabs) return oldGlobalTabs;
      const innerTabIndex = globalTab.innerTabs.findIndex(
        (innerTab) => innerTab.id === id
      );
      const tab = globalTab.innerTabs.find((innerTab) => innerTab.id === id);
      if (!tab) return oldGlobalTabs;
      rendererService.closeInnerTab(tab);
      const nextTabIndex = getNextTabIndex(globalTab.innerTabs, innerTabIndex);
      const nextActiveInnerTab = globalTab.innerTabs[nextTabIndex];
      navigateInnerTab(globalTab.innerTabs[nextTabIndex], globalTab);
      globalTab?.closedInnerTabs?.push(tab);
      const innerTabs = globalTab.innerTabs?.filter(
        (innerTab: IInnerTab) => innerTab.id !== id
      );
      if (!innerTabs) return oldGlobalTabs;
      globalTab.innerTabs = innerTabs;
      globalTab.activeInnerTabId = nextActiveInnerTab.id;
      return oldGlobalTabs.map((gTab) =>
        gTab.id === activeGlobalTabId ? globalTab : gTab
      );
    });
  };

  const closeGlobalActiveTab = () => {
    if (activeGlobalTabId === 0) return;
    closeGlobalTab(activeGlobalTabId);
  };

  const getActiveGlobalTab = (): IGlobalTab => {
    return getGlobalTabById(activeGlobalTabId);
  };

  const updateGlobalTabPayload = (id: number, payloadData: any) => {
    const tab = getGlobalTabById(id);
    if (tab) {
      setGlobalTabs((oldGlobalTabs) =>
        oldGlobalTabs.map((globalTab) =>
          globalTab.id === id
            ? {
                ...globalTab,
                payload: {
                  ...globalTab.payload,
                  ...payloadData,
                },
              }
            : globalTab
        )
      );
    }
  };

  const updateTabProperty = (
    senderTab: IGlobalTab,
    tabField: string,
    fieldValue: string
  ) => {
    setGlobalTabs((oldGlobalTabs) =>
      oldGlobalTabs.map((oldGlobalTab) => {
        if (oldGlobalTab.id !== senderTab.id) return oldGlobalTab;
        oldGlobalTab.innerTabs.forEach((innerTab) => {
          if (
            innerTab.id === senderTab.activeInnerTabId &&
            Object.keys(innerTab).includes(tabField)
          ) {
            (innerTab as any)[tabField] = fieldValue;
          }
        });

        return {
          ...oldGlobalTab,
          [tabField]: fieldValue,
        };
      })
    );
  };

  const updateGroupTabProperties = (
    tabId: IGlobalTab['id'],
    properties: Partial<IGlobalTab['group']>
  ) => {
    let dataToSet = undefined as IGlobalTab | undefined;
    const updatedTabs = globalTabsRefFromContext.current.map((t) => {
      const isCurrentTab = t.id === tabId;

      if (!t.group) return t;
      if (isCurrentTab) {
        dataToSet = {
          ...t,
          group: {
            ...t.group,
            ...(properties || {}),
          },
        };
        return dataToSet;
      }

      return t;
    });
    setGlobalTabs(updatedTabs);
    return dataToSet;
  };

  const handleCloseGroupTabsAnalytics = (tab: IGlobalTab) => {
    if (
      tab.group &&
      typeof trackEvent === 'function' &&
      (TabGroupName.PlaybookPresentation || TabGroupName.DemoPresent)
    ) {
      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EXIT_PRESENTATION, {
        location: 'global_tabs',
      });
    }

    if (tab.payload?.isOpenedFromEditor && tab.payload?.demo) {
      trackEvent(AnalyticsTrackEventName.DEMO_CLICK_EXIT_PREVIEW, {
        location: 'demo_preview_page',
        demoId: tab.payload.demo.id,
      });
    }
  };

  const handleCloseTabs = async (
    tab: IGlobalTab,
    groupRelatedTabs: IGlobalTab[],
    defaultTabToSwitchTo?: IGlobalTab
  ) => {
    let tabToSwitchTo = defaultTabToSwitchTo;
    const newGlobalTabs = globalTabsRefFromContext.current.filter(
      (t) => !groupRelatedTabs.includes(t)
    );
    handleCloseGroupTabsAnalytics(tab);

    const speakerPanelId = tab.group?.id;
    if (speakerPanelId) {
      await window.dsDesktop.speakerPanel.removeData(speakerPanelId);
    }

    const listOfPromises = groupRelatedTabs.map((t) => {
      return rendererService.closeGlobalTab(t);
    });
    await Promise.allSettled(listOfPromises);

    const activeGlobalTab = getActiveGlobalTab();

    const isPartOfActiveGroup = groupRelatedTabs.some(
      (t) => t.id === activeGlobalTab.id
    );
    const isDefaultTabToSwitchIsClosed =
      defaultTabToSwitchTo &&
      groupRelatedTabs.some((t) => t.id === defaultTabToSwitchTo.id);

    if (!tabToSwitchTo || isDefaultTabToSwitchIsClosed)
      tabToSwitchTo = isPartOfActiveGroup ? newGlobalTabs[0] : activeGlobalTab;
    switchTab(tabToSwitchTo, { globalTabs: newGlobalTabs });
  };

  const closeGroupTabsWithException = async (
    tabToCheck: IGlobalTab,
    ruleToCheck: (tab: IGlobalTab) => boolean,
    tabSwitchTo?: IGlobalTab
  ) => {
    if (isHomeTab(tabToCheck.id)) return false;
    const groupRelatedTabs = globalTabsRefFromContext.current.filter(
      (t) =>
        t?.group?.id === tabToCheck.group?.id &&
        (ruleToCheck(t) || t.id === tabToCheck.id)
    );
    return handleCloseTabs(tabToCheck, groupRelatedTabs, tabSwitchTo);
  };

  const closeGroupTabs = async (tab: IGlobalTab) => {
    if (isHomeTab(tab.id)) return;
    const groupRelatedTabs = globalTabsRefFromContext.current.filter(
      (t) => t.group && t?.group?.id === tab.group?.id
    );
    await handleCloseTabs(tab, groupRelatedTabs);
  };

  const toggleInnerViewsVisibility = ({
    activeTabId,
    show,
  }: {
    activeTabId: IGlobalTab['id'];
    show: boolean;
  }) => {
    globalTabsRefFromContext.current?.forEach((globalTab) => {
      if (globalTab.id !== activeTabId) {
        if (!globalTab.innerTabs.length) return;

        window.dsDesktop.layout.toggleInnerViewsVisibility({
          innerTabIds: globalTab.innerTabs.map((tab) => tab.id),
          show,
        });
      }
    });
  };

  return {
    createBasicTab,
    resetPersona,
    deletePersona,
    createCloneTab,
    createPreviewTab,
    createGlobalTab,
    createInnerTab,
    navigateGlobalTab,
    navigateInnerTab,
    switchGlobalTabSession,
    closeInnerTab,
    closeGlobalTab,
    closeGlobalActiveTab,
    getGlobalTabById,
    getActiveGlobalTab,
    updateGlobalTabPayload,
    updateTabProperty,
    updateGroupTabProperties,
    closeAllGlobalTabs,
    isHomeTab,
    globalTabs,
    closeGlobalTabs,
    getGlobalTabIndexById,
    closeGroupTabs,
    switchToHomeTab,
    toggleInnerViewsVisibility,
    closeGroupTabsWithException,
  };
}
